'use strict';

/**
 * UnionSearch class
 *
 * UnionSearch implements the dynamic (front-end) search features used on the liitot.fi website.
 *
 * @author Teppo Koivula / Avoine Oy (teppo.koivula@avoine.fi)
 */
class UnionSearch {

	/**
	 * Constructor method
	 */
	constructor() {
		this.initPolyfills();
		this.initCaches();
		this.initEvents();
		this.initIsotope();
	}

	/**
	 * Init runtime cache and store key objects for later use
	 */
	initCaches() {
		this.$header = $('#js-header');
		this.$form = $('#js-search-form');
		this.$suggestionsContainer = $('#js-search-suggestions-container');
		this.$suggestionsTooMany = $('#js-search-suggestions-too-many');
		this.$suggestionsIndustry = $('#js-search-suggestions-industry');
		this.$suggestionsList = $('#js-search-suggestions-list');
		this.$cards = $('#js-cards');
		this.$cardsNoResults = $('#js-cards-no-results');
		this.searchURL = this.$form.attr('action');
		this.searchCache = {};
		this.searchTimeout = null;
		this.removeTimeout = null;
		this.effectDuration = 350;
		this.scrollSpeed = 500;
	}

	/**
	 * Attach event listeners
	 */
	initEvents() {

		// Current object
		const self = this;

		// Events for search form.
		this.$form
			.on('submit', function(event) {
				// Prevent submitting the form
				event.preventDefault();
			})
			.on('click', 'button[type="submit"]', function() {
				// Prevent submitting the form and focus on search input instead
				$(this).prev('input').focus();
			})
			.on('change', 'input[name="by"]', function() {
				// Search type changed, show applicable search input(s).
				self.removeSuggestions();
				const searchType = $('input[name="by"]:checked').val();
				const $activeType = $(this).parent();
				self.$form.find('.js-search-type-label')
					.removeClass('active')
					.filter($activeType)
					.addClass('active');
				self.$form.find('.js-search-type').addClass('js-hide');
				$('#search_' + searchType).removeClass('js-hide');
			})
			.on('click', '.js-search-main-industry', function(event) {
				// Toggle main industy on or off.
				event.preventDefault();
				self.toggleMainIndustry($(this));
			})
			.on('keyup focus', '.js-search-input', function(event) {

				event.preventDefault();
				window.clearTimeout(self.searchTimeout);

				if (event.which == 9) {
					// Tab key pressed, do nothing.
					return;
				} else if (event.which == 40) {
					// Down arrow pressed, try to focus first suggestion.
					if (self.$suggestionsList.length) {
						self.$suggestionsList.find('.js-search-suggestion:first').focus();
					} else {
						return;
					}
				}

				// Perform a search operation.
				self.searchTimeout = window.setTimeout(function() {
					const searchType = self.$form.find('input[name="by"]:checked').val();
					const query = $('#' + searchType + '_search').val().trim();
					if (query) {
						const formData = self.$form.serialize();
						const cacheKey = self.searchURL + '?' + formData;
						if (Object.prototype.hasOwnProperty.call(self.searchCache, cacheKey)) {
							self.displaySuggestions(self.searchCache[cacheKey], cacheKey, searchType);
						} else {
							self.$form.find('.js-search-submit .icon')
								.removeClass('icon-search')
								.addClass('icon-spin6 animate-spin');
							$.get(self.searchURL + '?' + formData, function(data) {
								self.searchCache[cacheKey] = {
									'success': data.success || false,
									'suggestions': data.suggestions || []
								};
								self.displaySuggestions(self.searchCache[cacheKey], cacheKey, searchType);
							});
						}
					} else {
						self.removeSuggestions();
					}
				}, 250);
			})
			.on('keydown', '.js-search-suggestion:not(.disabled)', function(event) {
				if (event.which == 38 || event.which == 40) {
					// 38 = up arrow, 40 = down arrow
					event.preventDefault();
					const $parent = $(this).parent();
					const $link = event.which == 38 ? $parent.prev().find('a') : $parent.next().find('a');
					if ($link.length) {
						// Previous or next item found, focus on it.
						$link.focus();
					} else if (event.which == 38) {
						// Up arrow on first item: focus search input.
						self.$form.find('.js-search-type:not(.js-hide) input[type="text"]').focus();
					}
				}
			})
			.on('click', '.js-search-suggestion:not(.disabled)', function(event) {
				event.preventDefault();
				self.$form.find('.js-search-input:visible').val($(this).text());
				$.get($(this).attr('href'), function(data) {
					if (data.industries) {
						// Data contains employer info instead of unions; allow the user to choose
						// her own industry from the options available for this employer
						const $industryList = $('<ul>')
							.addClass('no-list md:flex flex-row flex-wrap')
							.appendTo(self.$suggestionsIndustry);
						data.industries.forEach(function(industry) {
							const $industryItem = $('<li>')
								.addClass('block md:inline-block')
								.appendTo($industryList);
							const $industryOption = $('<button>')
								.text(industry.industry)
								.addClass('w-full md:w-auto mb-half md:mr-half js-search-suggestion-industry')
								.attr('data-industry-code', industry.industry_code)
								.appendTo($industryItem);
						});
						self.$suggestionsIndustry
							.attr('tabindex', '-1')
							.removeClass('hidden')
							.focus();
					} else {
						self.handleSearchResponse(data);
					}
				});
			})
			.on('click', '.js-search-suggestion-industry', function(event) {
				// One of the industry optios available for a specific employer was clicked
				const industryCode = $(this).attr('data-industry-code');
				$.get(self.searchURL + '?action=find_union_industry_code&code=' + industryCode, function(data) {
					self.handleSearchResponse(data);
				});
			})
			.on('blur', '#js-search-suggestions-container, .js-search-suggestion, .js-search-input', function(event) {
				// Search suggestions blurred, see if search suggestions should be removed.
				event.stopPropagation();
				self.maybeRemoveSuggestions();
			});

		// Events for cards.
		this.$cards
			.on('click', '.js-toggle-details', function() {
				const $card = $(this).parents('.js-card:first');
				const $toggle = $(this).is('button') ? $(this) : $card.find('button.js-toggle-details:first');
				const $details = $card.find('.js-details');
				const expanded = !($toggle.attr('aria-expanded') == 'true');
				const label = $toggle.attr('data-toggle-label');
				$toggle
					.attr('aria-expanded', expanded)
					.attr('data-toggle-label', $toggle.text());
				$toggle.find('.js-toggle-details-label')
					.text(label);
				$toggle.find('.icon')
					.removeClass('icon-' + (expanded ? 'down' :'up') + '-open')
					.addClass('icon-' + (expanded ? 'up' :'down') + '-open');
				$details.toggle();
				self.$cards.isotope();
			});
	}

	/**
	 * Handle the response from employer (suggest) search
	 *
	 * @param {Object} data Query result.
	 */
	handleSearchResponse(data) {

		// Current object
		const self = this;

		// Show unions related to a specific employer
		self.removeSuggestions();
		self.$cards.show();
		let $visibleCards = $([]);
		self.$cards.find('.js-card:not([data-main-industry-promo])').each(function() {
			const $card = $(this);
			const id = $card.attr('data-id');
			$card.removeAttr('data-sort');
			if (data.filter(function(e) { return e.id == id; }).length) {
				$card.add('.js-card-visible');
				$visibleCards = $visibleCards.add($card);
			}
		}).promise().done(function() {
			let sortValue = 0;
			data.forEach(function(value) {
				self.$cards.find('#card_' + value.id).attr('data-sort', sortValue);
				sortValue++;
			});
			let $invisibleCards = self.$cards.find('.js-card:not(.js-card-visible)');
			if ($visibleCards.length) {
				$invisibleCards = $invisibleCards.add(self.$cardsNoResults);
			}
			$invisibleCards.slideUp(self.effectDuration).promise().done(function () {
				if ($visibleCards.length) {
					$visibleCards.removeClass('js-card-visible').slideDown(self.effectDuration).promise().done(function() {
						self.showResults();
					});
				} else {
					self.$cardsNoResults.slideDown(self.effectDuration, function() {
						self.showResults();
					});
				}
			});
		});
	}

	/**
	 * Init polyfills
	 */
	initPolyfills() {
		// Polyfill NodeList.forEAch for IE11:
		// https://developer.mozilla.org/en-US/docs/Web/API/NodeList/forEach#Polyfill
		if (window.NodeList && !NodeList.prototype.forEach) {
			NodeList.prototype.forEach = Array.prototype.forEach;
		}
	}

	/**
	 * Init Isotope/Masonry
	 */
	initIsotope() {
		this.$cards.addClass('-mx-half');
		$('.js-card').addClass('w-full lg:w-1/2');
		this.$cards.isotope({
			sortBy: ['number', 'original-order'],
			getSortData: {
				'number': '[data-sort]',
			},
			masonry: {
				itemSelector: '.js-card',
				columnWidth: '.js-card-sizer',
				percentPosition: true,
			},
		});
	}

	/**
	 * Toggle main industry on/off
	 *
	 * @param {jQuery} $toggle The toggle button.
	 */
	toggleMainIndustry($toggle) {

		// Current object
		const self = this;

		// Adjust toggle classes
		$toggle
			.toggleClass('active')
			.find('.icon')
			.toggleClass('icon-circle-empty icon-dot-circled');

		// Reset sort settings
		self.$cards.find('.js-card').removeAttr('data-sort');

		// Disable other toggles
		$toggle.siblings()
			.removeClass('active')
			.find('.icon')
			.removeClass('icon-dot-circled')
			.addClass('icon-circle-empty');

		if ($toggle.hasClass('active')) {

			// Main industry filter enabled, show/hide matching cards
			const mainIndustry = $toggle.attr('data-main-industry');
			$('.js-search-main-industry:not([data-main-industry="' + mainIndustry + '"]) .icon-dot-circled')
				.removeClass('icon-dot-circled')
				.addClass('icon-circle-empty');
			self.$cards.css('min-height', self.$cards.height() + 'px');
			let $visibleCards = $('.js-card[data-main-industry="' + mainIndustry + '"]');
			let $promoCards = $('.js-card[data-main-industry-promo="' + mainIndustry + '"]');
			if ($promoCards.length) {
				$visibleCards = $visibleCards.add($promoCards);
				$visibleCards = self.filterCards($visibleCards);
			}
			$('.js-card').not($visibleCards).add(this.$cardsNoResults).slideUp(self.effectDuration).promise().done(function() {
				$visibleCards.slideDown(self.effectDuration).promise().done(function() {
					self.showResults();
				});
			});

		} else {

			// No main industry filter, show all cards (except promo cards)
			$('.js-card[data-main-industry-promo]').slideUp(self.effectDuration).promise().done(function() {
				$('.js-card:not([data-main-industry-promo])').slideDown(self.effectDuration).promise().done(function() {
					self.showResults();
				});
			});

		}
	}

	/**
	 * Filter cards to only contain unique items
	 *
	 * @param {jQuery} $cards Unfiltered cards
	 * @returns {jQuery} Filtered cards
	 */
	filterCards($cards) {
		const ids = {};
		return $cards.filter(function() {
			const id = $(this).attr('data-id');
			if (ids[id]) {
				return false;
			} else {
				ids[id] = true;
				return true;
			}
		});
	}

	/**
	 * Display cards found using search
	 */
	showResults() {
		const self = this;
		this.$cards.css('min-height', 'auto');
		if (this.$cards.find('.js-card:visible').length) {
			this.$cardsNoResults.hide(function() {
				self.$cards.show(function() {
					self.$cards.isotope('updateSortData').isotope();
					self.scrollToCards();
				});
			});
		} else {
			this.$cardsNoResults.slideDown(this.effectDuration, function() {
				self.$cards.hide(function() {
					self.$cards.isotope();
					$([document.documentElement, document.body]).animate({
						scrollTop: self.$cardsNoResults.offset().top - self.$header.height() - parseInt(self.$cardsNoResults.css('paddingTop'))
					}, self.scrollSpeed);
				});
			});
		}
	}

	/**
	 * Scroll to the beginning of the cards section
	 */
	scrollToCards() {
		$([document.documentElement, document.body]).animate({
			scrollTop: this.$cards.offset().top - this.$header.height() - parseInt(this.$cards.css('paddingTop'))
		}, self.scrollSpeed);
	}

	/**
	 * Remove search suggestions
	 */
	removeSuggestions() {
		this.$suggestionsContainer
			.addClass('js-hide');
		this.$suggestionsIndustry
			.removeAttr('tabindex')
			.addClass('hidden')
			.find('ul').remove();
		this.$suggestionsTooMany
			.addClass('hidden');
		this.$suggestionsList.find('li')
			.remove();
		this.$form.find('.js-disabled-submit')
			.removeClass('js-disabled-submit')
			.removeAttr('tabindex');
	}

	/**
	 * Remove search suggestions with a slight delay unless suggestions list contains a focused node
	 */
	maybeRemoveSuggestions() {
		const self = this;
		this.removeTimeout = window.setTimeout(function() {
			if (
				!self.$suggestionsList.find(':focus').length
				&& !self.$form.find('.js-search-input:focus').length
				&& !self.$suggestionsIndustry.is(':focus')
				&& !self.$suggestionsIndustry.find('.js-search-suggestion-industry:focus')
			) {
				self.removeSuggestions();
			}
		}, 250);
	}

	/**
	 * Display list of suggestions
	 *
	 * @param {array} data Suggestions and the success state for this query.
	 * @param {string} cacheKey Current search cache key.
	 * @param {string} searchType Search type (industry or employer).
	 */
	displaySuggestions(data, cacheKey, searchType) {

		// Current object
		const self = this;

		if (self.$suggestionsContainer.data('cache-key') == cacheKey && !self.$suggestionsContainer.hasClass('js-hide')) {
			// Same search was performed already, do nothing.
			return;
		}

		// Set cache key and remove possible old suggestions.
		self.$suggestionsContainer.data('cache-key', cacheKey);
		this.removeSuggestions();

		if (!data.suggestions.length) {
			// Bail out early if no suggestions were found.
			return;
		}

		const queryWasSuccesful = data.success;
		data.suggestions.forEach(function(suggestion) {

			// Define suggestion item href attribute
			let href = self.searchURL + '?action=';
			if (searchType == 'employer') {
				href += 'find_union_employer&cid=' + suggestion.data;
			}

			// Create containing list item and the suggestion item itself
			const $list_item = $('<li></li>')
				.addClass('first:border-t-0 border-t border-gray')
				.appendTo(self.$suggestionsList);
			let $item;
			if (queryWasSuccesful) {
				$item = $('<a></a>')
					.attr('href', href)
					.addClass('js-search-suggestion block p-4 focus:bg-gray-200 hover:bg-gray-200');
			} else {
				$item = $('<span></span>')
					.attr('tabindex', 0)
					.addClass('js-search-suggestion disabled block p-4 no-underline text-gray-800');
			}
			$item
				.text(typeof suggestion == 'object' ? suggestion.value : suggestion)
				.appendTo($list_item);
		});

		// Show or hide the "too many results" info box
		this.$suggestionsTooMany.toggleClass('hidden', data.suggestions.length < 50);

		// Disable form submit button and show suggestions
		this.$form.find('button[type="submit"]')
			.attr('tabindex', -1)
			.addClass('js-disabled-submit');
		this.$suggestionsContainer.removeClass('js-hide');

		// Remove loading animation from submit button
		this.$form.find('.js-search-submit:visible .icon')
			.removeClass('icon-spin6 animate-spin')
			.addClass('icon-search');
	}

}
